import React from 'react';
import styled from 'styled-components';
import LearnMoreButton from './LearnMoreButton';
import TrendBand, { VIEW as BAND_VIEW } from './hero/TrendBand';
import { MessStack } from './hero/BeforeAfter';
import { DashboardApp } from './hero/DashboardApp';
import { easeDraw, easePop, mixHex, ramp } from './hero/motion';
import { useHeroTimeline } from './hero/useHeroTimeline';

const ACCENT = '#FF6B00';
const INK = '#1c1c1c';

/* Scene boundaries, in seconds:
   mess (0–1.5), fix (1.15–2.15), converge (0–1.65), trend (1.65–3.05), land (3.05–4.65). */
const MESS_START = 0;
const MESS_STAGGER = 0.085;
const MESS_TRAVEL = 0.7;
const FIX_START = 1.15;
const TREND_START = 1.65;
const LAND_START = 3.05;
const DURATION = 4.65;
const MESS_COUNT = 6;
const TILE_COUNT = 5;
const TILE_STAGGER = 0.055;
const TILE_TRAVEL = 0.62;

const HeroSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  padding: clamp(3.5rem, 7vw, 6.5rem) 0 clamp(2rem, 4vw, 3.5rem);
  background-color: transparent;

  @media (max-width: 1100px) {
    padding-bottom: 1rem;
  }
`;

/* The page-wide `.container` class sets a `padding` shorthand, so the hero owns
   its own gutters rather than fighting it for the vertical rhythm. */
const Shell = styled.div`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 2rem;
`;

/* The flanks only earn their space when all three columns fit at full size;
   below that the hero collapses back to the centred column. */
const Stage = styled(Shell)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.45fr) minmax(0, 1fr);
  align-items: center;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  padding-bottom: clamp(2.5rem, 5vw, 4.5rem);

  /* The bottom padding only exists to separate the copy from the band, so it
     goes when the band does. */
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    padding-bottom: 0;
  }
`;

const Flank = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const CopyBlock = styled.div`
  text-align: center;
`;

/* Capped so the accent line ("Your Software Is.") holds one line inside the
   middle grid column; the first sentence wraps into two balanced lines. */
const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.2rem, 4.6vw, 3.75rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  text-wrap: balance;
  color: ${INK};
`;

const TitleLine = styled.span`
  display: block;
`;

const TitleAccent = styled(TitleLine)`
  font-weight: 800;
`;

const Rule = styled.div`
  width: min(400px, 62%);
  height: 5px;
  margin: clamp(1rem, 2vw, 1.6rem) auto 0;
  transform-origin: left center;

  @media (max-width: 480px) {
    height: 3px;
  }
`;

const Subtitle = styled.p`
  max-width: 820px;
  margin: clamp(1.5rem, 2.6vw, 2.1rem) auto 0;
  font-size: clamp(1rem, 1.65vw, 1.3125rem);
  line-height: 1.5;
  font-weight: 400;
  color: #4a4a4a;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  margin-top: clamp(1.75rem, 3vw, 2.75rem);

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

/* Holds the graphic's intrinsic ratio so nothing is cropped at desktop widths.
   The band slices off its own sides to hold that ratio, so below the flank
   breakpoint it drops out rather than showing two stranded cells. */
const Band = styled.div`
  width: 100%;
  aspect-ratio: ${BAND_VIEW.width} / ${BAND_VIEW.height};
  min-height: 150px;
  max-height: 230px;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const Hero: React.FC = () => {
  const t = useHeroTimeline(DURATION);

  const messProgress = Array.from({ length: MESS_COUNT }, (_, i) => {
    const start = MESS_START + i * MESS_STAGGER;
    return ramp(t, start, start + MESS_TRAVEL);
  });
  const fix = ramp(t, FIX_START, FIX_START + 0.9);

  const tileProgress = Array.from({ length: TILE_COUNT }, (_, i) => {
    const start = 0.25 + i * TILE_STAGGER;
    return ramp(t, start, start + TILE_TRAVEL);
  });

  const lock = ramp(t, 1.4, 1.6);
  const solid = easePop(lock);
  const rule = easeDraw(ramp(lock, 0.1, 1));
  const draw = easeDraw(ramp(t, TREND_START, TREND_START + 1.3));
  const cta = ramp(t, LAND_START + 0.1, LAND_START + 1.05);
  const camera = 1 + 0.03 * ramp(t, 0, DURATION);

  const accentInk = mixHex(mixHex(INK, '#ffffff', 0.35), ACCENT, solid);
  const primaryCta = easePop(ramp(cta, 0, 0.65));
  const secondaryCta = easePop(ramp(cta, 0.18, 0.85));

  const ctaStyle = (progress: number): React.CSSProperties => ({
    opacity: progress,
    transform: `translateY(${14 * (1 - progress)}px)`,
    pointerEvents: progress > 0.05 ? 'auto' : 'none',
  });

  return (
    <HeroSection>
      <Stage>
        <Flank>
          <MessStack progress={messProgress} />
        </Flank>

        <CopyBlock className="font--jakarta">
          <HeroTitle>
            <TitleLine>Your Workflow Isn&apos;t Broken.</TitleLine>
            <TitleAccent style={{ color: accentInk }}>Your Software Is.</TitleAccent>
          </HeroTitle>

          <Rule
            style={{
              background: mixHex('#d8d4d1', ACCENT, solid),
              transform: `scaleX(${rule})`,
            }}
          />

          <Subtitle>
            We build tools for people who have better things to do than fight with software.
          </Subtitle>

          <ButtonsContainer>
            <div style={ctaStyle(primaryCta)}>
              <LearnMoreButton
                serviceName="Custom Software Project"
                modalTitle="Start a Project"
                buttonText="Start a Project"
                isPrimary={true}
                hasArrow={false}
              />
            </div>
            <div style={ctaStyle(secondaryCta)}>
              <LearnMoreButton
                serviceName="Consultation"
                modalTitle="Book a Free Consultation"
                buttonText="Book a Free Consultation"
                isPrimary={false}
                hasArrow={true}
              />
            </div>
          </ButtonsContainer>
        </CopyBlock>

        <Flank>
          <DashboardApp progress={fix} />
        </Flank>
      </Stage>

      <Shell>
        <Band>
          <TrendBand
            tileProgress={tileProgress}
            lock={lock}
            draw={draw}
            camera={camera}
            accent={ACCENT}
          />
        </Band>
      </Shell>
    </HeroSection>
  );
};

export default Hero;
